import { useEffect, useRef, useState, type CSSProperties } from 'react';
import Quill, { type EmitterSource } from 'quill';
import 'quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const TOOLBAR = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link'],
  ['clean'],
];

function RichTextEditor({ value, onChange, placeholder, minHeight = 180 }: RichTextEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastHtml = useRef<string | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const placeholderRef = useRef(placeholder);
  const [sourceMode, setSourceMode] = useState(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    placeholderRef.current = placeholder;
  }, [placeholder]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const quill = new Quill(container, {
      theme: 'snow',
      placeholder: placeholderRef.current,
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = quill;

    const initialHtml = valueRef.current || '';
    if (initialHtml) {
      quill.clipboard.dangerouslyPasteHTML(0, initialHtml, 'silent');
    }
    lastHtml.current = quill.root.innerHTML;
    quill.root.setAttribute('aria-label', 'Description');

    const handleTextChange = (_delta: unknown, _oldDelta: unknown, source: EmitterSource) => {
      if (source !== 'user') return;
      const html = quill.root.innerHTML;
      if (html === lastHtml.current) return;
      lastHtml.current = html;
      onChangeRef.current(html);
    };

    quill.on('text-change', handleTextChange);
    return () => {
      quill.off('text-change', handleTextChange);
      quillRef.current = null;
      container.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || value === lastHtml.current) return;
    lastHtml.current = value;
    quill.setText('', 'silent');
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(0, value, 'silent');
    }
  }, [value]);

  return (
    <div
      className="rich-editor"
      style={{ '--rich-editor-min-height': `${minHeight}px` } as CSSProperties}
    >
      <div className="rich-editor__mode">
        <button
          type="button"
          className="btn btn--sm btn--secondary"
          aria-pressed={sourceMode}
          onClick={() => setSourceMode((enabled) => !enabled)}
        >
          {sourceMode ? 'Visual editor' : 'HTML source'}
        </button>
      </div>
      <div ref={containerRef} className="rich-editor__visual" hidden={sourceMode} />
      {sourceMode && (
        <textarea
          className="rich-editor__source"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Edit raw HTML…"
          aria-label="Description HTML source"
          spellCheck={false}
        />
      )}
    </div>
  );
}

export default RichTextEditor;
